import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import logger from '../../utils/logger';
import './WritingDictation.css';

const WritingDictation = ({ onBack, selectedLanguage = 'English' }) => {
  const { t } = useTranslation('writingDictationManagement');
  const [activePath, setActivePath] = useState(null);
  const [activeSchoolLevel, setActiveSchoolLevel] = useState(null);
  const [activeYear, setActiveYear] = useState(null);
  const [showDictationForm, setShowDictationForm] = useState(false);
  const [dictationFormData, setDictationFormData] = useState({
    title: '',
    text_content: '',
    school_level: 'primary-school',
    year: 1
  });
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [formMessage, setFormMessage] = useState({ type: '', text: '' });
  const [dictationArticles, setDictationArticles] = useState([]);
  const [isLoadingArticles, setIsLoadingArticles] = useState(false);

  // State for edit form
  const [editingArticle, setEditingArticle] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: '',
    text_content: '',
    school_level: 'primary-school',
    year: 1
  });

  // Fetch articles when school level, year, or selected language changes
  useEffect(() => {
    if (activeSchoolLevel && activeYear) {
      const yearNumber = activeYear.split('-').pop();
      fetchDictationArticles(activeSchoolLevel, yearNumber);
    }
  }, [activeSchoolLevel, activeYear, selectedLanguage]);

  // ✅ ENGLISH KEYS (for API & database) - NEVER TRANSLATE THESE
  const SCHOOL_KEYS = {
    'primary-school': 'Primary School',
    'middle-school': 'Middle School',
    'high-school': 'High School'
  };

  // Writing & Dictation Paths Configuration - UI labels (translated)
  const writingDictationPaths = [
    {
      id: 'educational-levels',
      title: t('paths.educationalLevels.title'),
      icon: '📚',
      description: t('paths.educationalLevels.description'),
      color: '#007bff'
    },
    {
      id: 'international-exam',
      title: t('paths.internationalExam.title'),
      icon: '🌍',
      description: t('paths.internationalExam.description'),
      color: '#28a745'
    },
    {
      id: 'esp',
      title: t('paths.esp.title'),
      icon: '🎓',
      description: t('paths.esp.description'),
      color: '#ffc107'
    },
    {
      id: 'general',
      title: t('paths.general.title'),
      icon: '📖',
      description: t('paths.general.description'),
      color: '#6f42c1'
    }
  ];

  // School levels and years configuration - UI labels (translated) + English keys (for API)
  const schoolLevels = [
    {
      id: 'primary-school',
      name: t('schoolLevels.primarySchool'), // UI label (translated)
      key: SCHOOL_KEYS['primary-school'], // English key for API
      years: [t('yearLevels.year1'), t('yearLevels.year2'), t('yearLevels.year3')]
    },
    {
      id: 'middle-school',
      name: t('schoolLevels.middleSchool'), // UI label (translated)
      key: SCHOOL_KEYS['middle-school'], // English key for API
      years: [t('yearLevels.year1'), t('yearLevels.year2'), t('yearLevels.year3')]
    },
    {
      id: 'high-school',
      name: t('schoolLevels.highSchool'), // UI label (translated)
      key: SCHOOL_KEYS['high-school'], // English key for API
      years: [t('yearLevels.year1'), t('yearLevels.year2'), t('yearLevels.year3')]
    }
  ];

  // Handle form input changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setDictationFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle edit button click
  const handleEditClick = (article) => {
    setEditingArticle(article);
    setEditFormData({
      title: article.title,
      text_content: article.text_content,
      school_level: article.school_level,
      year: article.year
    });
    setShowEditForm(true);
  };

  // Handle delete button click
  const handleDeleteClick = async (articleId) => {
    if (window.confirm(t('messages.deleteConfirm'))) {
      try {
        const response = await fetch(`/api/dictation/articles/${articleId}`, {
          method: 'DELETE'
        });
        
        const result = await response.json();
        
        if (result.success) {
          // Refresh the articles list
          if (activeSchoolLevel && activeYear) {
            const yearNumber = activeYear.split('-').pop();
            fetchDictationArticles(activeSchoolLevel, yearNumber);
          }
          setFormMessage({ type: 'success', text: t('messages.articleDeleted') });
        } else {
          setFormMessage({ type: 'error', text: `Error: ${result.error}` });
        }
      } catch (error) {
        logger.error('Error deleting article:', error);
        setFormMessage({ type: 'error', text: t('messages.errorDeleting') });
      }
    }
  };

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editFormData.title || !editFormData.text_content || !editFormData.school_level || !editFormData.year) {
      setFormMessage({ type: 'error', text: t('messages.fillAllFields') });
      return;
    }

    try {
      const response = await fetch(`/api/dictation/articles/${editingArticle.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: editFormData.title,
          text_content: editFormData.text_content,
          school_level: editFormData.school_level,
          year: editFormData.year,
          language: selectedLanguage || 'English'
        })
      });

      const result = await response.json();

      if (result.success) {
        setFormMessage({ type: 'success', text: t('messages.articleUpdated') });
        setShowEditForm(false);
        setEditingArticle(null);
        
        // Refresh the articles list
        if (activeSchoolLevel && activeYear) {
          const yearNumber = activeYear.split('-').pop();
          fetchDictationArticles(activeSchoolLevel, yearNumber);
        }
      } else {
        setFormMessage({ type: 'error', text: `Error: ${result.error}` });
      }
    } catch (error) {
      logger.error('Error updating article:', error);
      setFormMessage({ type: 'error', text: 'Failed to update article. Please try again.' });
    }
  };

  // Handle edit form changes
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Fetch dictation articles for the current school level and year
  const fetchDictationArticles = async (schoolLevel, year) => {
    if (!schoolLevel || !year) return;
    
    logger.log('🔍 Fetching articles for:', { schoolLevel, year, language: selectedLanguage });
    setIsLoadingArticles(true);
    try {
      const params = new URLSearchParams({ school_level: schoolLevel, year });
      if (selectedLanguage) params.set('language', selectedLanguage);
      const response = await fetch(`/api/dictation/articles?${params.toString()}`);
      const result = await response.json();
      
      logger.log('📡 Fetch response:', result);
      
      if (result.success) {
        logger.log('✅ Articles fetched successfully:', result.data);
        setDictationArticles(result.data);
      } else {
        logger.error('❌ Failed to fetch articles:', result.error);
      }
    } catch (error) {
      logger.error('❌ Error fetching articles:', error);
    } finally {
      setIsLoadingArticles(false);
    }
  };

  // Handle form submission and TTS generation
  const handleSubmitDictation = async (e) => {
    e.preventDefault();
    
    if (!dictationFormData.title || !dictationFormData.text_content || !dictationFormData.school_level || !dictationFormData.year) {
      setFormMessage({ type: 'error', text: t('messages.fillAllFields') });
      return;
    }

    setIsGeneratingAudio(true);
    setFormMessage({ type: '', text: '' });

    try {
      const response = await fetch('/api/dictation/articles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: dictationFormData.title,
          text_content: dictationFormData.text_content,
          school_level: dictationFormData.school_level,
          year: dictationFormData.year,
          language: selectedLanguage || 'English'
        })
      });

      const result = await response.json();

      if (result.success) {
        if (result.warning) {
          // If there's a warning (audio generation failed), show it but don't close form
          setFormMessage({ type: 'warning', text: t('messages.articleCreatedWithWarning', { warning: result.warning }) });
          // Reset form for another attempt
          setDictationFormData({
            title: '',
            text_content: '',
            school_level: 'primary-school',
            year: 1
          });
        } else {
          // Success with audio - show success message and close form after delay
          setFormMessage({ type: 'success', text: `✅ ${t('messages.articleCreated')}` });
          
          logger.log('🎉 Article created successfully, refreshing articles list...');
          logger.log('📍 Current state:', { activeSchoolLevel, activeYear });
          
          // Refresh the articles list
          if (activeSchoolLevel && activeYear) {
            const yearNumber = activeYear.split('-').pop();
            logger.log('🔄 Calling fetchDictationArticles with:', { activeSchoolLevel, yearNumber });
            fetchDictationArticles(activeSchoolLevel, yearNumber);
          } else {
            logger.log('❌ Missing activeSchoolLevel or activeYear:', { activeSchoolLevel, activeYear });
          }
          
          setTimeout(() => {
            setShowDictationForm(false);
            setFormMessage({ type: '', text: '' });
            setDictationFormData({
              title: '',
              text_content: '',
              school_level: 'primary-school',
              year: 1
            });
          }, 2000); // Close form after 2 seconds
        }
      } else {
        setFormMessage({ type: 'error', text: `Error: ${result.error}` });
      }
    } catch (error) {
      logger.error('Error creating dictation article:', error);
      setFormMessage({ type: 'error', text: t('messages.errorCreating') });
    } finally {
      setIsGeneratingAudio(false);
    }
  };

  // Render the 4-path selection screen
  const renderPathSelection = () => (
    <div className="writing-dictation-paths-selection">
      <div className="admin-back-navigation">
        <button className="back-to-admin-btn" onClick={onBack}>
          <span className="material-icons">arrow_back</span>
          {t('backToCourseManagement')}
        </button>
      </div>
      
      <div className="paths-header">
        <h1>✍️ {t('title')}</h1>
        <p>{t('selectPath')}</p>
      </div>
      
      <div className="paths-grid">
        {writingDictationPaths.map(path => (
          <div 
            key={path.id} 
            className="path-card"
            onClick={() => setActivePath(path.id)}
            style={{ '--path-color': path.color }}
          >
            <div className="path-icon">{path.icon}</div>
            <h3>{path.title}</h3>
            <p>{path.description}</p>
            <div className="path-arrow">
              <span className="material-icons">arrow_forward</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render year-based tabs for Educational Levels
  const renderYearTabs = () => (
    <div className="year-tabs-section">
      <h3>{t('messages.selectYear')}</h3>
      <div className="year-tabs">
        {schoolLevels.find(level => level.id === activeSchoolLevel)?.years.map((year, index) => (
          <button
            key={index}
            className={`year-tab ${activeYear === `${activeSchoolLevel}-${index + 1}` ? 'active' : ''}`}
            onClick={() => setActiveYear(`${activeSchoolLevel}-${index + 1}`)}
          >
            {year}
          </button>
        ))}
      </div>
      
      {activeYear && (
        <div className="year-content">
          <h4>{t('messages.yearContent', { number: activeYear.split('-')[1] })}</h4>
          <button 
            className="add-dictation-btn"
            onClick={() => setShowDictationForm(true)}
          >
            <span className="material-icons">add</span>
            {t('actions.addDictationArticle')}
          </button>
        </div>
      )}
    </div>
  );

  // Render dictation creation form
  const renderDictationForm = () => (
    <div className="dictation-form-overlay">
      <div className="dictation-form-modal">
        <div className="form-header">
          <h3>{t('form.addNewArticle')}</h3>
          <button 
            className="close-form-btn"
            onClick={() => setShowDictationForm(false)}
          >
            <span className="material-icons">close</span>
          </button>
        </div>
        
        <form onSubmit={handleSubmitDictation} className="dictation-form">
          <div className="form-group">
            <label htmlFor="title">{t('form.title')} *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={dictationFormData.title}
              onChange={handleFormChange}
              required
              placeholder={t('form.titlePlaceholder')}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="text_content">{t('form.textContent')} *</label>
            <textarea
              id="text_content"
              name="text_content"
              value={dictationFormData.text_content}
              onChange={handleFormChange}
              required
              placeholder={t('form.textContentPlaceholder')}
              rows="8"
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="school_level">{t('form.schoolLevel')}</label>
              <select
                id="school_level"
                name="school_level"
                value={dictationFormData.school_level}
                onChange={handleFormChange}
              >
                <option value="">{t('form.selectSchoolLevel')}</option>
                {schoolLevels.map(level => (
                  <option key={level.id} value={level.id}>{level.name}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="year">{t('form.year')}</label>
              <select
                id="year"
                name="year"
                value={dictationFormData.year}
                onChange={handleFormChange}
              >
                <option value="">{t('form.selectYear')}</option>
                {activeSchoolLevel && schoolLevels.find(l => l.id === activeSchoolLevel)?.years.map((year, index) => (
                  <option key={index} value={index + 1}>{year}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Form Message Display */}
          {formMessage.text && (
            <div className={`form-message ${formMessage.type}`}>
              {formMessage.text}
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={() => setShowDictationForm(false)}>
              {t('actions.cancel')}
            </button>
            <button 
              type="submit" 
              className="submit-btn" 
              disabled={isGeneratingAudio}
            >
              {isGeneratingAudio ? (
                <>
                  <span className="rotating">⏳</span> {t('messages.generatingAudio')}
                </>
              ) : (
                t('actions.submit')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // Render dictation article cards
  const renderArticleCards = () => {
    if (isLoadingArticles) {
      return (
        <div className="articles-loading">
          <div className="loading-spinner">⏳</div>
          <p>{t('messages.loadingArticles')}</p>
        </div>
      );
    }

    if (dictationArticles.length === 0) {
      return (
        <div className="no-articles">
          <p>{t('messages.noArticlesYet')}</p>
          <p>{t('messages.addFirstArticle')}</p>
        </div>
      );
    }

    return (
      <div className="articles-grid">
        {dictationArticles.map(article => {
          logger.log('📋 Article data:', article);
          return (
            <div key={article.id} className="article-card">
              <div className="article-header">
                <h4>{article.title}</h4>
                <span className="article-status published">Published</span>
              </div>
              <div className="article-content">
                <div className="article-text-preview">
                  <p className="article-text">
                    {article.text_content && article.text_content.length > 80 
                      ? `${article.text_content.substring(0, 80)}...` 
                      : (article.text_content || 'No text content available')
                    }
                  </p>
                </div>
                
                <div className="article-actions">
                  <button className="edit-btn" onClick={() => handleEditClick(article)}>
                    <span className="material-icons">edit</span>
                    {t('actions.edit')}
                  </button>
                  <button className="delete-btn" onClick={() => handleDeleteClick(article.id)}>
                    <span className="material-icons">delete</span>
                    {t('actions.delete')}
                  </button>
                </div>
                
                <div className="article-meta">
                  <div className="meta-row">
                    <span className="meta-item">
                      <span className="material-icons">school</span>
                      {article.school_level ? article.school_level.replace('-school', ' School') : 'Unknown Level'}
                    </span>
                    <span className="meta-item">
                      <span className="material-icons">schedule</span>
                      Year {article.year || 'Unknown'}
                    </span>
                  </div>
                  <div className="meta-row">
                    <span className="meta-item">
                      <span className="material-icons">audiotrack</span>
                      {article.audio_path ? 'Audio Ready' : 'No Audio'}
                    </span>
                    <span className="meta-item">
                      <span className="material-icons">calendar_today</span>
                      {article.created_at ? new Date(article.created_at).toLocaleDateString() : 'Today'}
                    </span>
                  </div>
                  {article.audio_path && (
                    <div className="meta-row">
                      <span className="meta-item file-path">
                        <span className="material-icons">folder</span>
                        {article.audio_path.split('uploads/audio/')[1] || 'File path'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render Edit Dictation Article Form
  const renderEditDictationForm = () => (
    <div className="dictation-form-overlay">
      <div className="dictation-form-modal">
        <div className="form-header">
          <h3>{t('form.editArticle')}</h3>
          <button 
            className="close-form-btn"
            onClick={() => setShowEditForm(false)}
          >
            <span className="material-icons">close</span>
          </button>
        </div>
        
        <form onSubmit={handleEditSubmit} className="dictation-form">
          <div className="form-group">
            <label htmlFor="edit_title">Title *</label>
            <input
              type="text"
              id="edit_title"
              name="title"
              value={editFormData.title}
              onChange={handleEditFormChange}
              required
              placeholder="Enter dictation title"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="edit_text_content">{t('form.textContent')} *</label>
            <textarea
              id="edit_text_content"
              name="text_content"
              value={editFormData.text_content}
              onChange={handleEditFormChange}
              required
              placeholder={t('form.textContentPlaceholder')}
              rows="8"
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="edit_school_level">{t('form.schoolLevel')}</label>
              <select
                id="edit_school_level"
                name="school_level"
                value={editFormData.school_level}
                onChange={handleEditFormChange}
              >
                <option value="">{t('form.selectSchoolLevel')}</option>
                {schoolLevels.map(level => (
                  <option key={level.id} value={level.id}>{level.name}</option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="edit_year">{t('form.year')}</label>
              <select
                id="edit_year"
                name="year"
                value={editFormData.year}
                onChange={handleEditFormChange}
              >
                <option value="">{t('form.selectYear')}</option>
                {activeSchoolLevel && schoolLevels.find(l => l.id === activeSchoolLevel)?.years.map((year, index) => (
                  <option key={index} value={index + 1}>{year}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Form Message Display */}
          {formMessage.text && (
            <div className={`form-message ${formMessage.type}`}>
              {formMessage.text}
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={() => setShowEditForm(false)}>
              {t('actions.cancel')}
            </button>
            <button 
              type="submit" 
              className="submit-btn" 
            >
              {t('actions.update')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // Render Educational Levels content
  const renderEducationalLevelsContent = () => (
    <div className="writing-dictation-tab-content">
      <div className="writing-dictation-header">
        <h2>📚 {t('paths.educationalLevels.title')} & Dictation</h2>
        <p>{t('paths.educationalLevels.description')}</p>
      </div>
      
      {!activeSchoolLevel ? (
        <div className="school-levels-section">
          <h3>School Levels</h3>
          <div className="school-cards">
            {schoolLevels.map(level => (
              <div key={level.id} className="school-card">
                <h4>{level.name}</h4>
                <p>Basic writing skills and simple dictation exercises</p>
                <button 
                  className="manage-school-btn"
                  onClick={() => setActiveSchoolLevel(level.id)}
                >
                  Manage Content
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="school-content-section">
          <div className="school-navigation">
            <button 
              className="back-to-schools-btn"
              onClick={() => {
                setActiveSchoolLevel(null);
                setActiveYear(null);
              }}
            >
              <span className="material-icons">arrow_back</span>
              {t('backToWritingPaths')}
            </button>
            <h3>{schoolLevels.find(level => level.id === activeSchoolLevel)?.name}</h3>
          </div>
          
          {renderYearTabs()}
          {renderArticleCards()}
        </div>
      )}
    </div>
  );

  // Render International Exam content
  const renderInternationalExamContent = () => (
    <div className="writing-dictation-tab-content">
      <div className="writing-dictation-header">
        <h2>🌍 {t('paths.internationalExam.title')} & Dictation</h2>
        <p>{t('paths.internationalExam.description')}</p>
      </div>
      
      <div className="exam-types-section">
        <h3>Exam Types</h3>
        <div className="exam-cards">
          <div className="exam-card">
            <h4>IELTS</h4>
            <p>International English Language Testing System</p>
            <button className="manage-exam-btn">Manage Content</button>
          </div>
          <div className="exam-card">
            <h4>TOEFL</h4>
            <p>Test of English as a Foreign Language</p>
            <button className="manage-exam-btn">Manage Content</button>
          </div>
          <div className="exam-card">
            <h4>Cambridge</h4>
            <p>Cambridge English Qualifications</p>
            <button className="manage-exam-btn">Manage Content</button>
          </div>
        </div>
      </div>
    </div>
  );

  // Render ESP content
  const renderESPContent = () => (
    <div className="writing-dictation-tab-content">
      <div className="writing-dictation-header">
        <h2>🎓 {t('paths.esp.title')} & Dictation</h2>
        <p>{t('paths.esp.description')}</p>
      </div>
      
      <div className="esp-faculties-section">
        <h3>Academic Faculties</h3>
        <div className="faculty-cards">
          <div className="faculty-card">
            <h4>Medicine</h4>
            <p>Medical terminology and healthcare writing</p>
            <button className="manage-faculty-btn">Manage Content</button>
          </div>
          <div className="faculty-card">
            <h4>Engineering</h4>
            <p>Technical and engineering writing practice</p>
            <button className="manage-faculty-btn">Manage Content</button>
          </div>
          <div className="faculty-card">
            <h4>Business</h4>
            <p>Business and commerce writing exercises</p>
            <button className="manage-faculty-btn">Manage Content</button>
          </div>
        </div>
      </div>
    </div>
  );

  // Render General content
  const renderGeneralContent = () => (
    <div className="writing-dictation-tab-content">
      <div className="writing-dictation-header">
        <h2>📖 {t('paths.general.title')} & Dictation</h2>
        <p>{t('paths.general.description')}</p>
      </div>
      
      <div className="general-categories-section">
        <h3>Content Categories</h3>
        <div className="category-cards">
          <div className="category-card">
            <h4>Stories</h4>
            <p>Fictional and narrative writing practice</p>
            <button className="manage-category-btn">Manage Content</button>
          </div>
          <div className="category-card">
            <h4>News</h4>
            <p>Current events and news writing exercises</p>
            <button className="manage-category-btn">Manage Content</button>
          </div>
          <div className="category-card">
            <h4>Blogs</h4>
            <p>Informal and conversational writing practice</p>
            <button className="manage-category-btn">Manage Content</button>
          </div>
          <div className="category-card">
            <h4>Dialogues</h4>
            <p>Conversational and speaking practice</p>
            <button className="manage-category-btn">Manage Content</button>
          </div>
        </div>
      </div>
    </div>
  );

  // Render content based on active path
  const renderContent = () => {
    if (activePath === 'educational-levels') {
      return renderEducationalLevelsContent();
    } else if (activePath === 'international-exam') {
      return renderInternationalExamContent();
    } else if (activePath === 'esp') {
      return renderESPContent();
    } else if (activePath === 'general') {
      return renderGeneralContent();
    }
    return null;
  };

  // Main render logic
  if (!activePath) {
    return renderPathSelection();
  }

  return (
    <div className="writing-dictation-management-scroll-wrapper">
      <div className="writing-dictation-management-page">
        <div className="path-navigation">
          <button className="back-to-paths-btn" onClick={() => setActivePath(null)}>
            <span className="material-icons">arrow_back</span>
            {t('backToWritingPaths')}
          </button>
        </div>
        
        {renderContent()}
        {showDictationForm && renderDictationForm()}
        {showEditForm && renderEditDictationForm()}
      </div>
    </div>
  );
};

export default WritingDictation;
